const FEATURE_LABELS = {
  driver_module: "Driver module",
  file_manager: "File manager",
  notifications: "Notifications",
};

function humanizeKey(key) {
  if (!key) return "";
  if (FEATURE_LABELS[key]) return FEATURE_LABELS[key];
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function parseBadges(badgeLabel) {
  if (!badgeLabel) return [];
  return String(badgeLabel)
    .split(",")
    .map((b) => b.trim())
    .filter(Boolean);
}

export function getMonthlyPrice(plan) {
  const prices = plan?.pricing?.prices ?? [];
  const match =
    prices.find((p) => p.frequency === "monthly") ||
    prices.find((p) => p.is_default) ||
    prices[0];

  if (!match) {
    const fee = plan?.monthly_fee != null ? Number.parseFloat(plan.monthly_fee) : NaN;
    if (!Number.isNaN(fee)) {
      return {
        amount: fee,
        currency: (plan?.pricing?.currency || "usd").toUpperCase(),
      };
    }
    return null;
  }

  const amount = Number.parseFloat(match.price);
  if (Number.isNaN(amount)) return null;

  return {
    amount,
    currency: (match.currency || plan?.pricing?.currency || "usd").toUpperCase(),
  };
}

export function formatMoney(amount, currency = "USD") {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `$${amount}`;
  }
}

export function displayPrice(plan) {
  const monthly = getMonthlyPrice(plan);

  if (monthly) {
    return {
      amount: monthly.amount,
      currency: monthly.currency,
      suffix: "/ month",
      note: "Billed monthly · Cancel anytime",
      hasPrice: true,
    };
  }

  return {
    amount: null,
    currency: "USD",
    suffix: "",
    note: "Custom pricing for your lab",
    hasPrice: false,
  };
}

export function buildPlanFeatureLines(plan) {
  if (plan?.marketing_includes) {
    return [String(plan.marketing_includes)];
  }

  const lines = [];
  const limits = plan?.feature_limits || {};

  const slip = plan.slip_capacity ?? limits.slip_capacity;
  if (slip != null) {
    lines.push(`${Number(slip).toLocaleString()} slips per cycle`);
  }

  const storage = plan.storage_included_gb ?? limits.included_storage_gb;
  if (storage != null) {
    lines.push(`${storage} GB storage`);
  }

  const admins = plan.included_admins ?? limits.max_admin_seats;
  if (admins != null) {
    lines.push(`${admins} admin seat${admins === 1 ? "" : "s"}`);
  }

  const users = plan.max_user_seats ?? limits.max_user_seats;
  if (users != null) {
    lines.push(`up to ${users} user seat${users === 1 ? "" : "s"}`);
  }

  const enabledFeatures = (plan.features || []).filter((f) => f.enabled);
  for (const f of enabledFeatures) {
    lines.push(humanizeKey(f.feature_key));
  }

  return lines;
}

export function isHighlightedPlan(plan) {
  const badges = parseBadges(plan?.badge_label).map((b) => b.toLowerCase());
  return badges.some((b) => b.includes("popular") || b.includes("recommended"));
}
